import fp from 'lodash/fp';
import * as actions from './actions';
import * as constants from './constants';

export const INITIAL_STATE = {
    columns: constants.columns,
    currencies: [],
    fxRates: {},
    loading: false,
    pricingParametersInfoLoaded: false,
    rowMappings: {},
    pricingParametersRows: [],
    templates: [],
    templateSaved: false,
    templateInProcessOfSaving: false,
    templateNamesLoaded: false,
    pricingImModelView: 'SIMM',
    imModel: {
        pricingParameters: null,
        alternativePricingParameters: null
    }
};

const setCurrencies = fp.set('currencies');
const setLoading = fp.set('loading');
const setRows = fp.set('pricingParametersRows');
const setPricingParametersInfoLoaded = fp.set('pricingParametersInfoLoaded');
const setRowMappings = fp.set('rowMappings');
const setTemplates = fp.set('templates');
const setTemplateSaved = fp.set('templateSaved');
const setTemplateInProcessOfSaving = fp.set('templateInProcessOfSaving');
const setTemplateNamesLoaded = fp.set('templateNamesLoaded');


export default function reducer(state = INITIAL_STATE, action = {}) {
    switch (action.type) {
        case actions.SET_LOADING: {
            return setLoading(action.loading, state);
        }

        case actions.TOGGLE_PRICING_PARAMETERS:{
            return {
                ...state,
                pricingImModelView: action.imModel
            };
        }
        case actions.SET_CURRENCIES: {
            return setCurrencies(action.currencies, state);
        }
        case actions.SET_TEMPLATES_NAMES_LOADED: {
            return setTemplateNamesLoaded(action.names, state);
        }
        case actions.SET_PRICING_PARAMETERS_ROWS: {
            return setRows(action.data, state);
        }
        case actions.SET_TEMPLATE_SAVED: {
            return setTemplateSaved(action.templateSaved, state);
        }
        case actions.SET_TEMPLATE_IN_PROCESS_OF_SAVING: {
            return setTemplateInProcessOfSaving(action.inProcess, state);
        }
        case actions.SET_FX_RATES: {
            return fp.set(`fxRates.${action.date}`, action.rates, state);
        }
        case actions.DERIVE_AMOUNT_USD: {
            return state;
        }
        case actions.SET_IM_MODEL_PRICING_PARAMS:{
            return {...state, imModel: action.params}
        }
        case actions.ADD_PRICING_PARAMETERS_ROW: {
            const nextRowId = state.pricingParametersRows.length ? fp.max(state.pricingParametersRows.map(fp.get('id'))) + 1 : 0;

            const previousTradeId = state.pricingParametersRows.length
                ? state.pricingParametersRows[state.pricingParametersRows.length - 1].tradeId : '';

            const previousImModel = state.pricingParametersRows.length
                ? state.pricingParametersRows[state.pricingParametersRows.length - 1].imModel : '';

            const previousProdClass = state.pricingParametersRows.length
                ? state.pricingParametersRows[state.pricingParametersRows.length - 1].prodClass : '';

            const previousRiskType = state.pricingParametersRows.length
                ? state.pricingParametersRows[state.pricingParametersRows.length - 1].riskType : '';

            const copyPreviousValues = fp.flow(
                fp.set('tradeId', previousTradeId),
                fp.set('imModel', previousImModel),
                fp.set('prodClass', previousProdClass),
                fp.set('riskType', previousRiskType),
                fp.set('id', nextRowId),
            );
            return setRows([...state.pricingParametersRows,
                copyPreviousValues(constants.defaultRow)], state);
        }
        case actions.DELETE_PRICING_PARAMETERS_ROW: {
            return setRows(
                state.pricingParametersRows
                    .filter(row => row.id !== action.id)
                    .map((row, index) => fp.set('id', index)(row)),
                state
            );
        }
        case actions.REMOVE_TOGGLE_PRICING_PARAMETERS:
            {
                return {
                    ...state,
                    imModel: {
                        pricingParameters: null,
                        alternativePricingParameters: null
                    }, pricingImModelView: 'SIMM',
                };
            }
        case actions.REMOVE_ALL_ROWS: {
            return setRows([], state);
        }
        case actions.SET_PRICING_PARAMETERS_INFO_LOADED: {
            return setPricingParametersInfoLoaded(action.loaded, state);
        }
        case actions.SET_TEMPLATES: {
            return setTemplates(action.templates, state);
        }
        case actions.COPY_TRADE_ID: {
            if (state.pricingParametersRows.length < 1) {
                return state;
            }
            const tradeIdToCopy = state.pricingParametersRows[0].tradeId;
            return {
                ...state,
                pricingParametersRows: state.pricingParametersRows.map(row => fp.set('tradeId', tradeIdToCopy, row))
            };
        }
        case actions.LOAD_SELECTED_ROWS: {
            const templatesWithIds = action.rows.map((row, index) => fp.set('id', index, row));
            return setRows(templatesWithIds, state);
        }
        case actions.SET_ROW_MAPPINGS: {
            return setRowMappings(action.rowMappings, state);
        }
        case actions.DELETE_TEMPLATE: {
            return setTemplates(state.templates.filter(
                template => template.id !== action.templateId
            ), state);
        }
        case actions.SET_ACTIVE_VALUE: {
            const resetAllValues = fp.flow(
                fp.set('qualifier', ''),
                fp.set('labelOne', ''),
                fp.set('labelTwo', ''),
                fp.set('bucket', ''),
                fp.set('riskType', ''),
                fp.set('imModel', action.value),
            );

            const resetSomeValues = fp.flow(
                fp.set('qualifier', ''),
                fp.set('labelOne', ''),
                fp.set('labelTwo', ''),
                fp.set('bucket', ''),
                fp.set('riskType', action.value),
            );

            if (action.key === 'imModel') {
                return setRows(state.pricingParametersRows
                    .map(row => (row.id === action.rowId ? resetAllValues(row) : row)), state);
            }
            if (action.key === 'riskType') {
                return setRows(state.pricingParametersRows
                    .map(row => (row.id === action.rowId ? resetSomeValues(row) : row)), state);
            }
            return setRows(state.pricingParametersRows.map(row => (row.id === action.rowId
                ? fp.set(action.key, action.value, row) : row)), state);
        }
        default:
            return state;
    }
}
